import { css } from "@compiled/react";

import { JiraServiceManagementLogo, JiraLogo } from "@atlaskit/logo";
import { media } from "@atlaskit/primitives";

import { useSearchParameters } from "@/components/signup/hooks";
import {
    ProductKey,
    ServiceCollectionLogo,
    TeamworkCollectionLogo
} from "@/components/signup/utils";
import { wacToken } from "@/libraries/design/foundations";

const getLogo = (products: ProductKey[]) => {
    if (products.includes(ProductKey.TEAMWORK_COLLECTION)) {
        return <TeamworkCollectionLogo />;
    }
    if (products.includes(ProductKey.SERVICE_COLLECTION)) {
        return <ServiceCollectionLogo />;
    }
    if (products.includes(ProductKey.JIRA_SERVICE_MANAGEMENT)) {
        return <JiraServiceManagementLogo appearance="brand" />;
    }
    return <JiraLogo size="xlarge" appearance="brand" />;
};

export const TeamChoiceHeader = ({
    header,
    subheader,
    hideSubHeader = false,
    PreHeader,
    fadeOutContent = false
}: {
    header: string;
    subheader: string;
    hideSubHeader?: boolean; // go with experiment jsw_transparent_software_jtbd
    PreHeader?: () => JSX.Element;
    fadeOutContent?: boolean;
}) => {
    const searchParameters = useSearchParameters();
    const { products = [] } = searchParameters;

    // Cast products to ProductKey[] to satisfy getLogo's parameter type
    const productKeys = products as ProductKey[];

    return (
        <div css={[elevate, fadeOutContent && fade]}>
            {getLogo(productKeys)}
            <div css={headerContainer}>
                {PreHeader && <PreHeader />}
                <h1 css={headerFont}>{header}</h1>
                {!hideSubHeader && <h2 css={subheaderFont}>{subheader}</h2>}
            </div>
        </div>
    );
};

const fade = css({
    opacity: 0,
    transition: "opacity 0.5s ease-in-out"
});

const elevate = css({
    paddingBlockStart: "40px",
    gap: "28px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center"
});

const headerContainer = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "4px",
    textAlign: "center",
    padding: "0 40px"
});

const headerFont = css({
    fontWeight: "700",
    lineHeight: "48px",
    fontSize: "40px",
    fontFamily: wacToken("font.charlie.display"),
    margin: "0px",
    [media.below.md]: {
        lineHeight: "38px",
        fontSize: "32px"
    }
});

const subheaderFont = css({
    color: wacToken("color.primary.black"),
    lineHeight: "24px",
    fontWeight: "400",
    fontSize: "16px",
    fontFamily: wacToken("font.charlie.display"),
    margin: "0px",
    textAlign: "center",
    [media.below.md]: {
        lineHeight: "20px",
        fontSize: "14px"
    }
});
